import React, { useState, useRef, useEffect } from 'react';

const services = [
  {
    name: 'Battery',
    path: '/battery-replacement',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
        <rect x="14" y="8" width="20" height="34" rx="3" stroke="currentColor" strokeWidth="2.5" />
        <rect x="19" y="5" width="10" height="4" rx="1.5" fill="currentColor" opacity="0.4" />
        <rect x="18" y="24" width="12" height="14" rx="1.5" fill="currentColor" opacity="0.2" />
        <path d="M26 28L23 32H25.5L23 36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    name: 'Display',
    path: '/screen-replacement',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
        <rect x="12" y="6" width="24" height="36" rx="4" stroke="currentColor" strokeWidth="2.5" />
        <rect x="15" y="10" width="18" height="26" rx="1" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
        <circle cx="24" cy="40" r="1.5" fill="currentColor" opacity="0.4" />
        <path d="M16 18L22 24L16 30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
        <path d="M32 14L26 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
      </svg>
    ),
  },
  {
    name: 'Back Glass',
    path: '/screen-replacement',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
        <rect x="12" y="6" width="24" height="36" rx="4" stroke="currentColor" strokeWidth="2.5" />
        <rect x="15" y="9" width="18" height="8" rx="2" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
        <circle cx="20" cy="13" r="2" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
        <circle cx="26" cy="13" r="1.2" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
        <path d="M14 22L34 38" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
        <path d="M14 28L30 38" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.2" />
      </svg>
    ),
  },
  {
    name: 'Ear Speaker',
    path: '/speaker-mic-repair',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
        <rect x="14" y="6" width="20" height="36" rx="4" stroke="currentColor" strokeWidth="2.5" />
        <rect x="20" y="9" width="8" height="2" rx="1" fill="currentColor" opacity="0.6" />
        <path d="M20 22C20 22 22 20 24 20C26 20 28 22 28 22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M18 26C18 26 20.5 23 24 23C27.5 23 30 26 30 26" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.5" />
        <path d="M16 30C16 30 19 26 24 26C29 26 32 30 32 30" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.3" />
      </svg>
    ),
  },
];

const WhatAreYouLooking: React.FC<{ onSelect?: () => void }> = ({ onSelect }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="mb-6" ref={sectionRef}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1 h-5 bg-minto-500 rounded-full" />
        <h2 className="text-base md:text-lg font-semibold text-gray-900">What are you looking for?</h2>
      </div>

      {/* Professional card with image + icons */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-md overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Left/Top: Image */}
          <div className="relative md:w-2/5 h-40 md:h-auto overflow-hidden bg-gray-200">
            {isVisible && (
              <img
                src={require('../assets/HomeRepairService.png')}
                alt="Professional Mobile Repair Service"
                className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                width={600}
                height={400}
                loading="lazy"
                decoding="async"
                onLoad={() => setImageLoaded(true)}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/60 to-black/10" />
            <div className="absolute bottom-4 left-4">
              <p className="text-white text-sm md:text-base font-bold leading-tight">Expert Repair</p>
              <p className="text-white text-xs md:text-sm">at your doorstep</p>
            </div>
          </div>

          {/* Right/Bottom: Service Icons */}
          <div className="flex-1 p-4 md:p-6">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3 hidden md:block">Select a service</p>
            <div className="grid grid-cols-2 gap-2.5 md:gap-3">
              {services.map((service) => (
                <button
                  key={service.name}
                  onClick={() => onSelect?.()}
                  className="flex items-center gap-3 p-3 md:p-3.5 bg-gray-50 hover:bg-minto-50 rounded-lg border border-gray-100 hover:border-minto-200 transition-all duration-200 active:scale-95 group"
                >
                  <div className="w-9 h-9 md:w-10 md:h-10 flex-shrink-0 text-minto-500 group-hover:text-minto-600 transition-colors">
                    {service.icon}
                  </div>
                  <span className="text-xs md:text-sm font-semibold text-gray-800 text-left leading-tight">
                    {service.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatAreYouLooking;
